import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWeddingCategories } from '../../hooks/useWeddingCategories';
import { useWeddingVendors } from '../../hooks/useWeddingVendors';
import { useWeddingLocation } from '../../context/WeddingLocationContext';
import LocationPicker from '../../components/weddings/LocationPicker';
import * as LucideIcons from 'lucide-react';
import '@material/web/button/filled-button.js';
import '@material/web/button/outlined-button.js';

export default function WeddingLandingPage() {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const { location, setLocation } = useWeddingLocation();
    const { data: categories = [] } = useWeddingCategories();
    // Pass location into hooks if we want featured vendors to be location-aware too (Optional enhancement)
    const { data: allVendors = [] } = useWeddingVendors({
        lat: location?.lat,
        lon: location?.lon,
        radius: 50 // Default radius for landing page featured items
    });

    // Calculate vendor counts per category
    const categoryCounts = allVendors.reduce((acc, vendor) => {
        acc[vendor.category_id] = (acc[vendor.category_id] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/weddings/search?q=${encodeURIComponent(searchQuery.trim())}`);
        }
    };

    // Featured vendors: top 6 by rating across all categories
    const featuredVendors = [...allVendors]
        .sort((a, b) => b.rating - a.rating)
        .slice(0, 6);

    const getIcon = (iconName: string) => {
        const Icon = (LucideIcons as any)[iconName] || LucideIcons.HelpCircle;
        return <Icon size={24} />;
    };

    // Group top vendors by category
    const vendorsByCategory = categories.map((cat) => ({
        ...cat,
        vendors: allVendors
            .filter((v) => v.category_id === cat.id)
            .slice(0, 5) // Show top 5 per category
    }));

    return (
        <div className="min-h-screen bg-neutral-50 dark:bg-zinc-900">
            {/* Hero Section */}
            <section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url('https://images.unsplash.com/photo-1511795409834-ef04bbd61622?q=80&w=2069&auto=format&fit=crop')`,
                    }}
                >
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                </div>

                <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
                    <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight font-display">
                        Your Perfect Day Starts Here
                    </h1>
                    <p className="text-xl md:text-2xl text-white/90 mb-8 font-light">
                        Discover extraordinary venues, photographers, and creatives for your wedding.
                    </p>

                    {/* Search Bar */}
                    <div className="mb-8 max-w-2xl mx-auto bg-white/95 backdrop-blur rounded-2xl p-2 shadow-xl">
                        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-2">
                            {/* Location Input */}
                            <div className="flex-1 border-b md:border-b-0 md:border-r border-gray-200 p-2">
                                <LocationPicker
                                    onLocationSelect={(lat, lon, address) => {
                                        if (lat && lon) {
                                            setLocation({ lat, lon, address: address || '' });
                                        } else {
                                            setLocation(null); // Clear global location
                                        }
                                    }}
                                    initialAddress={location?.address}
                                    placeholder="Search city (e.g. New York)"
                                />
                            </div>

                            {/* Keyword Input */}
                            <div className="flex-[1.5] relative flex items-center p-2">
                                <LucideIcons.Search className="absolute left-4 text-gray-400" size={20} />
                                <input
                                    type="text"
                                    placeholder="Photographers, Venues..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-12 pr-4 py-2 bg-transparent outline-none text-gray-900 placeholder-gray-500"
                                />
                            </div>

                            <button
                                type="submit"
                                className="px-8 py-3 bg-rose-600 text-white rounded-xl font-medium hover:bg-rose-700 transition-colors shadow-lg"
                            >
                                Search
                            </button>
                        </form>
                    </div>

                    {/* Popular Searches */}
                    <div className="flex flex-wrap justify-center gap-2 mb-8 text-sm text-white/80">
                        <span>Popular:</span>
                        {['Photographers', 'Venues', 'Makeup Artists'].map((tag) => (
                            <button
                                key={tag}
                                onClick={() => setSearchQuery(tag)} // Simple pre-fill
                                className="hover:text-white hover:underline transition-colors"
                            >
                                {tag}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap justify-center gap-4">
                        <md-filled-button
                            onClick={() => {
                                const el = document.getElementById('browse-categories');
                                el?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            style={{ '--md-sys-color-primary': '#e11d48' } as React.CSSProperties}
                        >
                            Browse Vendors
                        </md-filled-button>
                        <md-outlined-button
                            onClick={() => navigate('/vendor/dashboard')}
                            style={{
                                '--md-outlined-button-outline-color': 'white',
                                '--md-outlined-button-label-text-color': 'white'
                            } as React.CSSProperties}
                        >
                            List Your Business
                        </md-outlined-button>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-16 px-4 max-w-7xl mx-auto">
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">How It Works</h2>
                    <p className="text-gray-500 dark:text-gray-400">Planning your wedding has never been easier</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { icon: 'Search', title: 'Discover', desc: 'Browse top-rated vendors, view portfolios, and find your perfect match.' },
                        { icon: 'MessageCircle', title: 'Connect', desc: 'Chat directly with vendors, discuss availability, and get quotes instantly.' },
                        { icon: 'CalendarHeart', title: 'Plan', desc: 'Book your favorites and organize your entire wedding timeline in one place.' }
                    ].map((item, idx) => (
                        <div key={idx} className="flex flex-col items-center text-center p-6 rounded-2xl bg-white dark:bg-zinc-800/50 border border-gray-100 dark:border-zinc-700/50">
                            <div className="w-16 h-16 bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-full flex items-center justify-center mb-4">
                                {getIcon(item.icon)}
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                            <p className="text-gray-500 dark:text-gray-400">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Quick Categories Grid */}
            <section id="browse-categories" className="py-12 px-4 max-w-7xl mx-auto -mt-20 relative z-20">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {categories.map((cat) => {
                        const count = categoryCounts[cat.id] || 0;
                        const isComingSoon = count === 0;

                        return (
                            <button
                                key={cat.id}
                                onClick={() => !isComingSoon && navigate(`/weddings/vendors/${cat.slug}`)}
                                disabled={isComingSoon}
                                className={`flex flex-col items-center justify-center p-6 bg-white/95 dark:bg-zinc-800/95 backdrop-blur shadow-lg rounded-xl transition-all duration-300 group
                                    ${isComingSoon ? 'opacity-70 cursor-not-allowed grayscale-[0.5]' : 'hover:bg-rose-50 dark:hover:bg-zinc-700 hover:-translate-y-1'}
                                `}
                            >
                                <div className={`text-rose-600 dark:text-rose-400 mb-3 ${!isComingSoon && 'group-hover:scale-110 transition-transform'}`}>
                                    {getIcon(cat.icon)}
                                </div>
                                <span className="font-semibold text-gray-800 dark:text-white text-sm">
                                    {cat.name}
                                </span>
                                {isComingSoon && (
                                    <span className="mt-2 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 text-[10px] uppercase font-bold tracking-wider rounded-full">
                                        Coming Soon
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* Featured Vendors Section */}
            {featuredVendors.length > 0 && (
                <section className="py-16 px-4 bg-gradient-to-b from-neutral-50 to-rose-50/30 dark:from-zinc-900 dark:to-zinc-800/50">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-10">
                            <span className="inline-block px-3 py-1 text-xs font-semibold text-rose-600 bg-rose-100 dark:bg-rose-900/30 dark:text-rose-400 rounded-full mb-3">
                                HANDPICKED FOR YOU
                            </span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
                                Featured Vendors
                            </h2>
                            <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-lg mx-auto">
                                Top-rated professionals trusted by hundreds of couples
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {featuredVendors.map((vendor) => (
                                <div
                                    key={vendor.id}
                                    onClick={() => navigate(`/weddings/vendor/${vendor.id}`)}
                                    className="group cursor-pointer bg-white dark:bg-zinc-800 rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-zinc-700"
                                >
                                    <div className="h-52 overflow-hidden relative">
                                        <img
                                            src={
                                                vendor.images?.[0] ||
                                                vendor.packages?.find((p: any) => p.images?.length > 0)?.images[0] ||
                                                'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=400'
                                            }
                                            alt={vendor.name}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                        <div className="absolute top-3 left-3 bg-rose-600 text-white text-xs font-bold px-2 py-1 rounded">
                                            FEATURED
                                        </div>
                                        <div className="absolute top-3 right-3 bg-white/90 dark:bg-black/80 backdrop-blur px-2 py-1 rounded-lg flex items-center gap-1 shadow-sm">
                                            <LucideIcons.Star size={14} className="text-amber-500 fill-amber-500" />
                                            <span className="text-xs font-bold text-gray-900 dark:text-white">{vendor.rating.toFixed(1)}</span>
                                        </div>
                                    </div>
                                    <div className="p-5">
                                        <span className="text-xs font-medium text-rose-600 dark:text-rose-400 uppercase tracking-wider">
                                            {categories.find(c => c.id === vendor.category_id)?.name || 'Vendor'}
                                        </span>
                                        <h3 className="font-bold text-lg text-gray-900 dark:text-white mt-1 mb-2 truncate">
                                            {vendor.name}
                                        </h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                                            <LucideIcons.MapPin size={14} className="text-rose-500" />
                                            {vendor.city || 'Location available on request'}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Horizontal Scrolling Sections for Each Category */}
            <div className="py-12 space-y-16">
                {vendorsByCategory.map((category) => (
                    category.vendors.length > 0 && (
                        <section key={category.id} className="block relative">
                            <div className="px-4 max-w-7xl mx-auto mb-6 flex justify-between items-end">
                                <div>
                                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                        {category.name}
                                    </h2>
                                    <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
                                        Top rated {category.name.toLowerCase()} for your wedding
                                    </p>
                                </div>
                                <button
                                    onClick={() => navigate(`/weddings/vendors/${category.slug}`)}
                                    className="text-rose-600 dark:text-rose-400 font-medium hover:text-rose-700 text-sm flex items-center gap-1"
                                >
                                    View All <LucideIcons.ArrowRight size={16} />
                                </button>
                            </div>

                            {/* Scroll Container */}
                            <div className="flex overflow-x-auto pb-8 px-4 gap-6 scrollbar-hide snap-x snap-mandatory max-w-[100vw]">
                                {/* Spacer for left padding in full width */}
                                <div className="w-0 md:w-[calc((100vw-80rem)/2)] flex-shrink-0" />

                                {category.vendors.map((vendor) => (
                                    <div
                                        key={vendor.id}
                                        onClick={() => navigate(`/weddings/vendor/${vendor.id}`)}
                                        className="snap-start flex-shrink-0 w-80 md:w-96 bg-white dark:bg-zinc-800 rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group border border-gray-100 dark:border-zinc-700"
                                    >
                                        <div className="h-56 overflow-hidden relative">
                                            <img
                                                src={
                                                    vendor.images?.[0] ||
                                                    vendor.packages?.find((p: any) => p.images?.length > 0)?.images[0] ||
                                                    'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=400'
                                                }
                                                alt={vendor.name}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                            <div className="absolute top-3 right-3 bg-white/90 dark:bg-black/80 backdrop-blur px-2 py-1 rounded-lg flex items-center gap-1 shadow-sm">
                                                <LucideIcons.Star size={14} className="text-amber-500 fill-amber-500" />
                                                <span className="text-xs font-bold text-gray-900 dark:text-white">{vendor.rating.toFixed(1)}</span>
                                            </div>
                                        </div>
                                        <div className="p-5">
                                            <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-1 truncate">
                                                {vendor.name}
                                            </h3>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mb-3">
                                                <LucideIcons.MapPin size={14} className="text-rose-500" />
                                                {vendor.city || 'Location available on request'}
                                            </p>

                                            {/* Price hint if available */}
                                            {vendor.packages && vendor.packages.length > 0 && (
                                                <div className="text-sm font-medium text-gray-900 dark:text-white bg-gray-50 dark:bg-zinc-700/50 inline-block px-2 py-1 rounded">
                                                    Starts at ₹{Math.min(...(vendor.packages?.map((p: any) => p.price) || [0])).toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                {/* "View More" Card */}
                                <div
                                    onClick={() => navigate(`/weddings/vendors/${category.slug}`)}
                                    className="snap-start flex-shrink-0 w-48 bg-gray-50 dark:bg-zinc-800/50 rounded-2xl border-2 border-dashed border-gray-200 dark:border-zinc-700 flex flex-col items-center justify-center cursor-pointer hover:border-rose-400 hover:bg-rose-50/50 transition-colors"
                                >
                                    <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 mb-3">
                                        <LucideIcons.ArrowRight size={20} />
                                    </div>
                                    <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                                        View all {category.name}
                                    </span>
                                </div>

                                <div className="w-4 flex-shrink-0" />
                            </div>
                        </section>
                    )
                ))}
            </div>

            {/* Newsletter / CTA Section */}
            <section className="py-20 px-4 bg-rose-900 text-white text-center">
                <div className="max-w-2xl mx-auto">
                    <LucideIcons.Heart className="w-12 h-12 mx-auto mb-6 text-rose-300" />
                    <h2 className="text-3xl font-bold mb-4">Ready to plan?</h2>
                    <p className="text-rose-100 mb-8 text-lg">
                        Join thousands of couples and vendors planning their perfect day.
                    </p>
                    <md-filled-button
                        onClick={() => navigate('/vendor/dashboard')}
                        style={{
                            '--md-filled-button-container-color': 'white',
                            '--md-filled-button-label-text-color': '#be123c',
                            '--md-filled-button-hover-state-layer-color': '#be123c'
                        } as React.CSSProperties}
                    >
                        List Your Business
                    </md-filled-button>
                </div>
            </section>
        </div >
    );
}
